
import LanguageSelectorDropdown from '@/app/components/LanguageSelectorDropdown.vue';
import Skeleton from '@/app/components/Skeleton.vue';
import ColumnHeader from '@/app/Survey/components/ColumnHeader.vue';
import DefaultAlert from '@/app/Survey/components/DefaultAlert.vue';
import DefaultContainer from '@/app/Survey/components/DefaultContainer.vue';
import Logotype from '@/app/Survey/components/Logotype.vue';
import MainLoading from '@/app/Survey/components/MainLoading.vue';
import PageSteps from '@/app/Survey/components/PageSteps.vue';
import CustomStylor from '@/app/Survey/config/CustomStylor';
import { themeByConfig } from '@/app/Survey/config/ThemeByConfig';
import FormRegisterComponent from '@/app/Survey/FormRegisterComponent';
import DataUrl from '@/app/Survey/model/DataUrl';
import MobileLayout from '@/app/Survey/model/MobileLayout';
import Steps from '@/app/Survey/model/Steps';
import { IConfig, ITheme, IThemeConfig } from '@/app/Survey/model/Theme';
import SplashScreen from '@/app/Survey/WidgetComponents/SplashScreen.vue';
import FormStateResponse from '@/dtos/FormStateResponse';
import IAlertConfig from '@/dtos/IAlertConfig';
import { getAnswers, removeData, setAnswer, setAnswers } from '@/helpers/LocalStorageService';
import { postMessageToWidget } from '@/helpers/PostMessageToWidget';
import DynamicLDService from '@/services/DynamicLDService';
import FormService from '@/services/FormService';
import { FormStructure, IAnswer, IColumn, IComponent, IPage, IQuestions, IRadioAnswer, IResponseAnswer } from '@/services/FormStructure';
import JourneyParameters, { JourneyParametersDefault } from '@/services/JourneyParameters';
import JourneyService from '@/services/JourneyService';
import TransactionService from '@/services/TransactionService';
import DeletePartialAnswersService from '@/services/DeletePartialAnswersService';
import { AxiosError, Cancel } from 'axios';
import ResizeObserver from 'resize-observer-polyfill';
import { Component, Inject, Mixins, Ref, Watch } from 'vue-property-decorator';
import Conditional from './model/conditionals/Conditional';
import ConditionalDto, { ConditionQuestionStateDto } from './model/conditionals/ConditionalDto';
import ExpiredState from './model/ExpiredState';
import { FormComponentsType, isQuestionComponent } from './model/Form';
import PageType from './model/PageType';
import TurnstileService from '@/services/TurnstileService';

const defaultFormStructure = {
    title: '',
    instanceName: '',
    config: { mobileLayout: MobileLayout.default, showLogotype: true, logo: '', pages: [] },
    appearance: { themes: [], mainColor: '', customStyle: '' },
    createdAt: '',
    employeeId: 0,
    id: 0,
    updatedAt: '',
};

@Component({ components: { Skeleton, ColumnHeader, PageSteps, LanguageSelectorDropdown, DefaultAlert, DefaultContainer, SplashScreen, MainLoading, Logotype } })
export default class Form extends Mixins(FormRegisterComponent) {
    //#region Services

    @Inject() public formService!: FormService;
    @Inject() public dynamicLDService!: DynamicLDService;
    @Inject() public journeyService!: JourneyService;
    @Inject() public transactionService!: TransactionService;
    @Inject() public turnstileService!: TurnstileService | null;
    public deletePartialAnswersService: DeletePartialAnswersService;

    //#endregion

    public formStructure: FormStructure = defaultFormStructure;
    public urlData: DataUrl = null;
    public questionsIdToRemove: number[] = [];
    public isMountingFormStructure: boolean = true;
    public journeyParameters: JourneyParameters = {};
    public questions: IQuestions = { answers: [] };
    public alertVisibility: boolean = false;
    public alertOptions: IAlertConfig = { status: '', type: '', text: '' };
    public width: number = 0;
    public customStylor: CustomStylor = null;
    public isMissingRequiredQuestion: boolean = false;
    public isLoading: boolean = false;
    private resizeObserver: ResizeObserver = null;
    public isLastVisiblePage: boolean = false;

    @Ref('defaultContainer')
    public defaultContainer!: DefaultContainer;

    //#region State
    public get isFormLoading(): boolean {
        return this.isMountingFormStructure && this.isStyleLoading;
    }

    public get isStyleLoading(): boolean {
        return this.customStylor?.isLoading ?? false;
    }

    public get pagesAmount(): number {
        return this.formStructure.config.pages.length;
    }

    public get pagesAmountWithoutEndingPage(): number {
        return this.formStructure.config.pages.filter(page => page.pageType !== 'endPage').length;
    }

    public get currentPage(): number {
        return parseInt((this.$route.query.page as string) ?? '1');
    }

    public get hasEndingPage(): boolean {
        return !!this.formStructure.config.pages.find(page => page.pageType === 'endPage');
    }

    public get isEndingPage(): boolean {
        return this.currentBuilderPage?.pageType === PageType.EndPage;
    }

    public set currentPage(page: number) {
        if (page === Number(this.$route?.query?.page)) return;

        this.$router.replace({
            name: 'page',
            query: { ...this.$route.query, page: page.toString() },
            params: { ...this.$route.params },
        });
    }

    public get transactionReference(): number | string {
        return this.urlData.transactionReference;
    }

    public get currentBuilderPage(): IPage {
        return this.formStructure.config.pages[this.currentPage - 1];
    }

    public get currentBuilderColumns(): IColumn[] {
        return this.currentBuilderPage?.columns;
    }

    public get formReady(): boolean {
        return Boolean(this.formStructure?.config?.pages?.length && this.currentBuilderPage?.columns?.length) && !this.isFormLoading;
    }

    public get currentConfigTheme(): IConfig {
        if (this.currentBuilderPage && this.currentBuilderPage.currentThemeId) {
            return this.formStructure.appearance.themes.find((theme: IThemeConfig) => theme.id === this.currentBuilderPage.currentThemeId)?.config;
        }
        return null;
    }

    public get theme(): ITheme | {} {
        return themeByConfig(this.currentConfigTheme);
    }

    public get shouldShowOnlyRequiredLabels(): boolean {
        return this.haveMoreOrEqualOptionalThanRequiredQuestionsAndAtLeastOneRequiredQuestion() || !this.totalOfOptionalQuestions();
    }

    public get haveAtLeastOneRequiredQuestion(): boolean {
        return !!this.totalOfRequiredQuestions();
    }

    public get isEnvironmentSandbox(): boolean {
        return this.urlData.environment === 'sandbox';
    }

    public get isWidget(): boolean {
        return this.urlData && this.urlData.mode === 'widget';
    }

    public get transactionData(): object {
        const transactionData = this.formStructure.transaction ?? {};
        if (this.isEnvironmentSandbox) {
            return { ...transactionData, labels: this.$route.query };
        }
        return transactionData;
    }

    private get allComponents(): IComponent[] {
        // Page > Column > Component(Question, text, label etc)
        return this.formStructure.config.pages.flatMap((page: IPage) => page.columns.flatMap((column: IColumn) => column.components.flatMap((item: IComponent) => item)));
    }

    private get endingPageNumber(): number {
        return this.formStructure.config.pages.find(page => page.pageType === 'endPage')?.page;
    }

    @Watch('currentPage')
    public onCurrentPageChanged(): void {
        this.isLastVisiblePage = this.validateIsLastPage();
    }

    //#endregion

    //#region Lifecycle
    public created(): void {
        window.addEventListener('resize', this.windowResized);
    }

    public destroyed(): void {
        window.removeEventListener('resize', this.windowResized);
        this.resizeObserver?.disconnect();
    }

    //#endregion

    //#region Resize

    public windowResized(): void {
        this.width = document.documentElement.clientWidth;
    }

    private initResizeObserver(): void {
        if (!this.defaultContainer) return;
        // @ts-ignore
        const defaultContainerContent = this.defaultContainer.defaultContainerContent;

        if (defaultContainerContent) {
            this.resizeObserver = new ResizeObserver(() => {
                this.sendFormHeightToWidget();
            });

            this.resizeObserver.observe(defaultContainerContent);
        }
    }

    public validateIsLastPage(): boolean {
        const nextPage = this.getPageBasedOnConditionals(Steps.next);
        return nextPage > this.pagesAmountWithoutEndingPage;
    }

    private sendFormHeightToWidget(): void {
        this.$nextTick(() => {
            if (!this.defaultContainer) return;
            // @ts-ignore
            const defaultContainerContent = this.defaultContainer.defaultContainerContent;
            const scrollHeight = defaultContainerContent ? defaultContainerContent.scrollHeight : document.documentElement.scrollHeight;
            postMessageToWidget('FORM_RESIZE', scrollHeight);
        });
    }
    //#endregion

    //#region sandbox
    private async populateSandboxDataDynamicLikeDislike(): Promise<void> {
        if (this.isEnvironmentSandbox) {
            for (const page of this.formStructure.config.pages) {
                for (const column of page?.columns) {
                    for (const component of column.components) {
                        if (component.settings?.childrenType === 'DynamicLikeDislikeComponent') {
                            component.children = await this.getSandboxDynamicLDClasses(component.id);
                        }
                    }
                }
            }
            this.updateFormByLocalData();
        }
    }

    private async getSandboxDynamicLDClasses(questionId: number): Promise<IComponent[]> {
        return await this.dynamicLDService.getSandboxDynamicLD(questionId);
    }
    //#endregion

    //#region initialize form

    public async mounted(): Promise<void> {
        this.customStylor = new CustomStylor(document);
        this.urlData = new DataUrl(this.$route);
        this.deletePartialAnswersService = new DeletePartialAnswersService(this.formService);

        if ((!this.urlData.transactionReference || !this.urlData.attemptId) && !this.isEnvironmentSandbox) {
            this.redirectWithParams('formUnavailable');
            return;
        }

        await this.getFormById();

        await Promise.all([this.validateAvailability(), this.validateSurveyIsExpired()]);

        await this.populateSandboxDataDynamicLikeDislike();

        postMessageToWidget('FORM_OPENED');

        this.windowResized();
        this.initResizeObserver();
    }

    private async validateAvailability(): Promise<void> {
        try {
            const formState = await this.formService.validateFormAvailability(this.urlData.formValidationSet);
            if (formState.isRatingCompleted) {
                postMessageToWidget('FORM_ERROR', 'alreadyAnswered');
                this.redirectWithParams('alreadyAnswered');
            }
            if (this.urlData.isUsingExternalReference) {
                this.overwriteSurveySet(formState);
            }
        } catch (err) {
            console.error(err);
            postMessageToWidget('FORM_ERROR', 'formUnavailable');
            this.redirectWithParams('formUnavailable');
        }
    }

    private async validateSurveyIsExpired(): Promise<void> {
        if (this.isEnvironmentSandbox) return;
        try {
            const expiredState: ExpiredState = await this.transactionService.getSurveyExpiredState(Number(this.urlData!.transactionId));
            if (expiredState?.isExpired) {
                this.redirectWithParams('formExpired');
            }
        } catch (err) {
            console.error(err);
        }
    }

    public async getFormById(): Promise<void> {
        try {
            let journeyParameters = null;
            [journeyParameters, this.formStructure] = await Promise.all([
                this.journeyService.getJourneyParametersByTransationId(Number(this.urlData!.transactionId)),
                this.formService.getFormById(Number(this.urlData!.formId), Number(this.urlData!.transactionId)),
            ]);
            this.journeyParameters = this.getJourneyParameters(journeyParameters);

            await this.setCustomBootstrapImport();
            this.initializeMultipleAnswerArrayAnswers();

            this.formationQuestions();
            
            //@ts-ignore
            this.$root.instanceName = this.formStructure.instanceName;
        } catch (err) {
            if ((err as AxiosError)?.response?.data?.message === 'Form not found') {
                this.redirectWithParams('formUnavailable');
                postMessageToWidget('FORM_ERROR', 'formUnavailable');
                return;
            }
            this.triggerAlert('danger', 'Error', '');
            postMessageToWidget('FORM_ERROR', err.toString());
        } finally {
            if (this.canRedirectToFirstPage()) this.resetPageToFirst();

            this.populateAnswersFromLocalData();

            // Prepare conditionals
            this.buildConditionals();
            this.isLastVisiblePage = this.validateIsLastPage();
            this.isMountingFormStructure = false;
        }
    }

    private canRedirectToFirstPage(): boolean {
        return this.$route.name === 'page' && this.$route.query?.page !== '1';
    }

    private populateAnswersFromLocalData() {
        if (getAnswers(this.transactionReference, this.urlData!.formId)) {
            this.updateFormByLocalData();
        }
    }

    public formationQuestions(): void {
        // What does it does?

        if (this.transactionReference) {
            this.questions['transactionId'] = Number(this.transactionReference);
            this.questions['attemptId'] = Number(this.urlData.attemptId);
        } else {
            this.questions = { ...this.questions, ...this.urlData.query };
        }
        this.questions['formId'] = this.urlData.formId;
    }

    public async setCustomBootstrapImport() {
        const color = this.formStructure?.appearance?.mainColor ?? '#0C5A7A';
        await this.customStylor.mountStyle(encodeURIComponent(color), this.formStructure?.appearance?.customStyle);
    }

    public initializeMultipleAnswerArrayAnswers(): void {
        this.formStructure.config.pages
            .flatMap(p => p.columns)
            .flatMap(c => c.components)
            .filter(c => c?.settings?.selectionStrategy === 'multiple')
            .forEach(c => (c.answer = []));
    }

    public getComponentWithoutConditionals(component: IComponent): Omit<IComponent, 'conditionals'> {
        const cleanedComponent = JSON.parse(JSON.stringify(component));
        delete cleanedComponent.conditionals;
        return cleanedComponent;
    }

    public getJourneyParameters(parameters: JourneyParameters): JourneyParameters {
        const journeyParametersDefault = new JourneyParametersDefault();
        Object.keys(journeyParametersDefault).forEach(parameterName => {
            if (parameters[parameterName] === null || parameters[parameterName] === undefined) {
                parameters[parameterName] = journeyParametersDefault[parameterName];
            }
        });
        return parameters;
    }

    public updateFormByLocalData(): void {
        for (const pages of this.formStructure.config.pages) {
            for (const column of pages.columns) {
                for (const component of column.components) {
                    const localAnswersList = getAnswers(this.transactionReference, this.urlData!.formId) as IAnswer[][];

                    localAnswersList?.forEach((answers: IAnswer[]) => {
                        answers.forEach((localAnswer: IAnswer) => {
                            const { answer, settings } = component;

                            const isSameComponent = isQuestionComponent(component) && localAnswer.formQuestionId === component.id;
                            const isChildAnswer = localAnswer?.parentId === component.id;

                            // Get answer already done for ParentGroupComponent
                            const isParentGroupComponent = component.type === FormComponentsType.ParentGroupComponent;
                            if (isChildAnswer && isParentGroupComponent) {
                                this.setParentGroupChildAnswers(component, localAnswer);
                            }

                            if (isSameComponent && !isParentGroupComponent) {
                                if (Array.isArray(answer) && settings.selectionStrategy === 'multiple') {
                                    answer.push(localAnswer);
                                } else {
                                    component.answer = localAnswer;
                                }
                            }
                        });
                    });
                }
            }
        }

        this.isLastVisiblePage = this.validateIsLastPage();
    }

    public setParentGroupChildAnswers(component: IComponent, answer: IAnswer): void {
        const componentIndex = component.children.findIndex((child: IComponent) => child.id === answer.formQuestionId);
        component.children[componentIndex] = { ...component.children[componentIndex], answer };
    }

    //#endregion

    //#region Conditionals

    public buildConditionals(): void {
        this.formStructure.config.pages.forEach((page: IPage) => {
            this.assignsConditionalsToPages(page);
            this.assignsConditionalsToQuestion(page);
        });
    }

    private assignsConditionalsToPages(page: IPage): void {
        const conditionals = this.formStructure?.conditionals || [];
        const pageConditionals = this.splitConditionsByElementType();
        page['conditionals'] = [];
        pageConditionals.forEach((item: ConditionalDto) => {
            if (item.targets.map(t => t.id).includes(page.id)) {
                page['conditionals'].push(new Conditional(conditionals[item.index]));
            }
        });
    }

    private assignsConditionalsToQuestion(page: IPage): void {
        const conditionals = this.formStructure?.conditionals || [];
        const questionsConditionals = this.splitConditionsByElementType();
        const componentList = page.columns.flatMap((column: IColumn) => column.components.flatMap((item: IComponent) => item));
        componentList.forEach((component: IComponent) => {
            component['conditionals'] = [];
            questionsConditionals.forEach((conditional: ConditionalDto) => {
                if (conditional.targets.map(t => t.id).includes(component.id)) {
                    const newConditional = new Conditional(conditionals[conditional.index]);
                    component['conditionals'].push(newConditional);
                }
            });
        });
    }

    public splitConditionsByElementType(): ConditionalDto[] {
        const questionList = this.allComponents;
        questionList.push(...questionList.flatMap(component => component.children ?? []));
        const conditionals = this.formStructure?.conditionals || [];

        const elementConditionals = [] as ConditionalDto[];
        conditionals.forEach((conditional: ConditionalDto, index: number) => {
            if (conditional.conditions.length > 0) {
                conditional.conditions.forEach((element: ConditionQuestionStateDto) => {
                    element.questionElement = questionList.filter(item => isQuestionComponent(item) && item.id === element.questionId)[0];
                });
                elementConditionals.push({ ...conditional, index });
            }
        });
        return elementConditionals;
    }

    public shouldShowItsSelf(conditionals: Conditional[]): boolean {
        if (!conditionals || conditionals.length === 0) {
            return true;
        }

        if (conditionals?.some((item: Conditional) => item?.action === 'hide')) {
            return !conditionals?.some((item: Conditional) => item?.shouldShowItsSelf() === false);
        }

        return conditionals?.some((item: Conditional) => item?.shouldShowItsSelf() === true);
    }

    public getPageBasedOnConditionals(pageDirection: string): number {
        const step = pageDirection === Steps.next ? 1 : -1;
        let pageIndex = this.currentPage + step;

        while (pageIndex >= 0 && pageIndex <= this.pagesAmount) {
            const page: IPage | undefined = this.getPageById(pageIndex);

            if (!page) break;

            const shouldPageShow = this.shouldShowItsSelf(page.conditionals ?? []);
            if (shouldPageShow) {
                return page.page;
            }

            pageIndex += step;
        }

        return step === -1 ? 1 : pageIndex;
    }

    //#endregion

    //#region pagination and column

    public getPageById(currentPage: number): IPage {
        const pagesAmount = this.formStructure.config.pages.length;
        if (currentPage < 1) currentPage = 1;
        if (currentPage >= pagesAmount) currentPage = pagesAmount;
        const objPage = this.formStructure?.config?.pages.filter(page => page.page === currentPage)[0];
        return objPage ?? ({} as IPage);
    }

    public pageSelect(step: string): void {
        if (this.haveAnyRequiredQuestionToAnswer() && step !== Steps.previous) {
            this.setIsMissingRequiredField();
            this.scrollToRequiredQuestion();
            return;
        }

        this.currentPage = this.getPageBasedOnConditionals(step);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    private resetPageToFirst(): void {
        this.$router.replace({
            name: 'page',
            query: { ...this.$route.query, page: '1' },
            params: { ...this.$route.params },
        });
    }

    public handleMobileColumnsOrder(columnNumber: number): string {
        const order: string = 'order-1';
        if (this.currentBuilderPage.columns.length > 1 && columnNumber === 1 && this.formStructure.config.mobileLayout === MobileLayout.secondUp) {
            return order;
        }
        return '';
    }

    public handleMobileHideColumn(columnNumber: number): boolean {
        if (this.currentBuilderPage.columns.length > 1 && this.width <= 415) {
            return !(
                (this.formStructure.config.mobileLayout === MobileLayout.hideFirstColumn && columnNumber === 1) ||
                (this.formStructure.config.mobileLayout === MobileLayout.hideSecondColumn && columnNumber === 2)
            );
        }
        return true;
    }
    //#endregion

    //#region Submission

    public async handleUpdatedComponent(component: IComponent, isInitial = false, isMainQuestion = false): Promise<void> {
        this.updateComponentInFormStructure(component);
        this.saveAnswer(component);
        this.buildConditionals();
        postMessageToWidget('FORM_QUESTIONANSWERED', component.id);
        this.isLastVisiblePage = this.validateIsLastPage();
        await this.sendAnswer(component, isInitial, isMainQuestion);
    }

    private updateComponentInFormStructure(component: IComponent) {
        for (const col of this.currentBuilderPage.columns) {
            col.components = col.components.map(c => (c.id === component.id && c.type === component.type ? component : c));
        }
    }

    private saveAnswer(component: IComponent): void {
        this.questionsIdToRemove = [];

        this.questions.formId = this.urlData.formId;
        this.questions.transactionId = this.urlData.transactionId;
        this.questions.attemptId = this.urlData.attemptId;

        if (Array.isArray(component?.answer) && component.settings.selectionStrategy === 'multiple') {
            this.questions.answers = [];
            if (component?.answer.length === 0) {
                this.questionsIdToRemove.push(component.id);
                this.questions.answers = [{ formQuestionId: component.id, answer: null, formId: this.urlData.formId }];
            } else {
                component?.answer.forEach((item: IAnswer) => {
                    const questionAnswer = { formQuestionId: component.id, ...item };
                    if (item?.id) questionAnswer.id = (component.answer as IAnswer).id;
                    this.questions.answers.push({ ...questionAnswer });
                });
            }
        } else {
            const objQuestionsAnswer = { formQuestionId: component.id, formId: this.urlData.formId, ...component.answer };
            this.questions.answers = [objQuestionsAnswer];

            if (component.parentId) {
                this.mountChildrenAnswer(component);
                const questions = this.allComponents;
                const componentIndex = questions.findIndex((item: IComponent) => isQuestionComponent(item) && item.id === component.parentId);
                this.setParentGroupChildAnswers(questions[componentIndex], objQuestionsAnswer as IAnswer);
            }

            if (!Array.isArray(component?.answer) && component.answer.id) this.questions.answers[0]['id'] = (component.answer as IAnswer).id;
        }
    }

    private mountChildrenAnswer(component: IComponent): void {
        this.questions.answers[0]['parentId'] = component.parentId;
        this.questions.answers[0]['isClass'] = component.isClass;
        if (component.isClass) {
            this.questions.answers[0]['questionTitle'] = component.content.title;
            this.questions.answers[0]['classId'] = component.classId;
            this.questions.answers[0]['classOrder'] = component.classOrder;
        }
    }

    private async sendAnswer(component: IComponent, isInitial = false, isMainQuestion = false): Promise<void> {
        try {
            const questions = JSON.parse(JSON.stringify(this.questions));
            const answer = this.newAnswerFromQuestion(questions);

            setAnswer(this.transactionReference, answer, component?.id, this.urlData!.formId);

            if (this.journeyParameters?.saveInitialRating) {
                await this.postPartialAnswer(component, questions, isInitial, isMainQuestion);
                await this.deleteUnwantedPartialAnswers();
            }

            component.answer = component.settings?.selectionStrategy === 'multiple' ? answer : answer[0];
        } catch (error) {
            if ((error as Cancel).message !== 'Canceled') {
                const errorMessage = 'Error save answer';
                console.error(errorMessage, error);
                this.triggerAlert('danger', errorMessage, '');
                postMessageToWidget('FORM_ERROR', errorMessage);
            }
        }
    }

    private async deleteUnwantedPartialAnswers(): Promise<void> {
        try {
            const answersStorage = getAnswers(this.transactionReference, this.urlData!.formId) ?? [];

            await this.deletePartialAnswersService.deleteUnwantedPartialAnswers({
                transactionReference: this.transactionReference,
                formId: this.urlData!.formId,
                questionIds: this.questionsIdToRemove ?? [],
                answersStorage,
                allComponents: this.allComponents,
                shouldShowItsSelf: this.shouldShowItsSelf.bind(this),
            });
        } catch (error) {
            console.error('Error to delete partial answers', error);
            postMessageToWidget('FORM_ERROR', error.toString());
            throw error;
        }
    }

    private alreadyPostedMainQuestion: boolean = false;

    private async postPartialAnswer(updatedComponent: IComponent, questions: IQuestions, isInitial = false, isMainQuestion = false): Promise<void> {
        const token = isInitial ? await this.getInitialToken() : null;

        // is not initial, which means user clicks in some answer, then resolve the turnstile token to null
        if (!isInitial) this.turnstileService?.skipValidation();

        // This prevents a race condition scenario where the user might rapidly change their score selection
        // BEFORE the Turnstile token has finished resolving, which could result in sending an outdated
        // or incorrect score value to the server instead of the user's final intended selection
        if (isMainQuestion) {
            if (isInitial && this.alreadyPostedMainQuestion) return;
            this.alreadyPostedMainQuestion = true;
        }

        if (this.isButtonSelectComponentAndSingleAnswer(updatedComponent)) {
            this.formService.cancelSaveAnswer();
            await this.formService.cancelableSaveAnswer(questions, token);
        } else {
            await this.formService.saveAnswer(questions, token);
        }
    }

    private async getInitialToken() {
        try {
            return await this.turnstileService?.awaitToken();
        } catch (error) {
            console.error('Could not generate turnstile token, bypassing verification:', error);
            return null;
        }
    }

    private isButtonSelectComponentAndSingleAnswer(updatedComponent: IComponent): boolean {
        return updatedComponent?.type === FormComponentsType.ButtonSelectComponent && updatedComponent?.settings?.selectionStrategy === 'single';
    }

    private newAnswerFromQuestion(question: IQuestions): IResponseAnswer[] {
        return question.answers.reduce((newAnswer, answer) => {
            newAnswer.push({
                id: Date.now(),
                answer: answer.answer,
                formQuestionId: answer.formQuestionId,
                text: answer.text,
                parentId: answer?.parentId,
                transactionId: question.transactionId,
                attemptId: question.attemptId,
                classId: answer.classId,
                classOrder: answer.classOrder,
                questionTitle: answer.questionTitle,
                isClass: answer.isClass,
                formId: this.urlData.formId,
            });
            return newAnswer;
        }, []);
    }

    public async submitFormComplete(): Promise<void> {
        if (this.haveAnyRequiredQuestionToAnswer()) {
            this.setIsMissingRequiredField();
            this.scrollToRequiredQuestion();
            return;
        }

        try {
            this.isLoading = true;
            if (this.transactionReference) await this.handleSendAllAnswers();

            await this.goToEndingPage();
            this.isLoading = false;
            removeData(this.transactionReference, this.urlData!.formId);
            postMessageToWidget('FORM_COMPLETED');
        } catch (error) {
            console.error('Error...', error);
            this.isLoading = false;
            this.triggerAlert('danger', 'Error on complete form', '');
            postMessageToWidget('FORM_ERROR', error.toString());
        }
    }

    private async handleSendAllAnswers(): Promise<void> {
        const localAnswersList = getAnswers(this.transactionReference, this.urlData!.formId) as IResponseAnswer[][];
        if (localAnswersList?.length) {
            await this.sendAllAnswers(localAnswersList.flat());
        }
    }

    private async sendAllAnswers(localAnswers: IResponseAnswer[]): Promise<IResponseAnswer[]> {
        const transactionId = this.urlData.transactionId ?? localAnswers[0]?.transactionId;
        const attemptId = this.urlData.attemptId ?? localAnswers[0]?.attemptId;
        localAnswers.forEach((item: IResponseAnswer) => delete item.id);
        const answer = {
            answers: localAnswers,
            transactionId,
            attemptId,
            formId: this.urlData.formId,
        } as IQuestions;
        return await this.formService.saveAnswer(answer, null, true);
    }

    private async goToEndingPage(): Promise<void> {
        if (this.hasEndingPage) this.currentPage = this.endingPageNumber;
        else this.$router.push({ name: 'defaultEndingPage', params: { ...this.$route.params } });
    }

    //#endregion

    //#region Validation

    private haveAnyRequiredQuestionToAnswer(): boolean {
        this.isMissingRequiredQuestion = false;
        const requiredComponents = this.getVisibleRequiredComponents();
        return requiredComponents.some(component => !this.isComponentAnswered(component));
    }

    private getVisibleRequiredComponents(): IComponent[] {
        return this.currentBuilderPage.columns.flatMap(column => column.components.filter(component => this.isThisQuestionRequired(component)));
    }

    public isComponentAnswered(component: IComponent): boolean {
        const refs = this.$refs[`form-component-${component.elementId}`] as Vue[];
        if (!refs || !refs[0]) return false;

        if (component.type === FormComponentsType.ParentGroupComponent) {
            return (refs[0] as any).isAllChildrenAnswered;
        }
        return (refs[0] as any).isAnswered;
    }

    private setIsMissingRequiredField(): void {
        this.isMissingRequiredQuestion = this.haveAnyRequiredQuestionToAnswer();
    }

    public scrollToRequiredQuestion(): void {
        setTimeout(() => {
            const invalidFeedbackStyleElement = document.getElementsByClassName('required-field')[0];
            if (invalidFeedbackStyleElement) {
                invalidFeedbackStyleElement.scrollIntoView({ behavior: 'smooth' });
            }
        }, 200);
    }

    public isThisQuestionRequired(component: IComponent): boolean {
        return (component.settings?.isRequired || false) && this.shouldShowItsSelf(component.conditionals);
    }

    private haveMoreOrEqualOptionalThanRequiredQuestionsAndAtLeastOneRequiredQuestion(): boolean {
        return this.haveMoreOrEqualOptionalThanRequiredQuestions() && !!this.totalOfRequiredQuestions();
    }

    private haveMoreOrEqualOptionalThanRequiredQuestions(): boolean {
        return this.totalOfRequiredQuestions() <= this.totalOfOptionalQuestions();
    }

    private totalOfRequiredQuestions(): number {
        return this.currentBuilderPage.columns.map(column => column.components.map(component => component?.settings?.isRequired))[0].filter(questions => questions === true).length;
    }

    private totalOfOptionalQuestions(): number {
        return this.currentBuilderPage.columns.map(column => column.components.map(component => component?.settings?.isRequired))[0].filter(questions => questions !== true).length;
    }

    //#endregion

    //#region others
    public sortComponentsByOrder(components: IComponent[]): IComponent[] {
        if (!components || !components.length) return components;
        return components.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public setLang(selectedLang: string): void {
        this.$i18n.locale = selectedLang;
    }

    private redirectWithParams(name: string): void {
        this.$router.replace({ name, params: { ...this.$route.params } });
    }

    private overwriteSurveySet(formState: FormStateResponse): void {
        this.urlData = new DataUrl({
            ...this.$route,
            query: {
                ...this.$route.query,
                transactionId: String(formState.transactionId),
                attemptId: String(formState.attemptId),
            },
        });
    }

    public getColumnStyle(column: number): { [key: string]: string } | {} {
        if (Object.keys(this.theme).length) {
            if (this.currentBuilderPage.columns.length > 1) return column === 1 ? (this.theme as ITheme).leftColumn : (this.theme as ITheme).rightColumn;
            return (this.theme as ITheme)?.centralColumn || (this.theme as ITheme).rightColumn;
        }
        return {};
    }

    public getCornerOrientation(columnSide: number): string {
        if (this.currentBuilderPage.columns.length === 1) return 'rounded';
        if (columnSide === 1) return 'rounded-left';
        if (columnSide === 2) return 'rounded-right';
    }

    public triggerAlert(type: string, status: string, text: string): void {
        this.alertOptions = { type, status, text };
        this.alertVisibility = true;
        setTimeout(() => {
            this.alertVisibility = false;
        }, 2000);
    }

    //#endregion
}
